import { RemotePlayer, SpeechBubble, getStatusColor } from './types';

export class GameRenderer {
    private playerSize: number;
    private _frameTime: number = 0;
    private _colorCache = new Map<string, string>();
    private _textWidthCache = new Map<string, number>();
    private _mapCanvas: HTMLCanvasElement | null = null;
    private _bubbleLineCache = new Map<string, string[]>();

    constructor(playerSize: number) {
        this.playerSize = playerSize;
    }

    /** 맵 이미지를 오프스크린 캔버스에 사전 렌더링 (매 프레임 Image 디코딩 방지) */
    public cacheMapImage(mapImage: HTMLImageElement) {
        if (!mapImage.naturalWidth || !mapImage.naturalHeight) {
            this._mapCanvas = null;
            return;
        }
        const c = document.createElement('canvas');
        c.width = mapImage.naturalWidth;
        c.height = mapImage.naturalHeight;
        const ctx = c.getContext('2d')!;
        ctx.drawImage(mapImage, 0, 0);
        this._mapCanvas = c;
    }

    /** 맵 캐시 무효화 (센터 전환 시 호출) */
    public invalidateMapCache() {
        this._mapCanvas = null;
    }

    public renderScene(
        ctx: CanvasRenderingContext2D,
        canvas: HTMLCanvasElement,
        state: {
            cameraX: number; cameraY: number;
            mapLoaded: boolean; mapImage: HTMLImageElement | null;
            MAP_WIDTH: number; MAP_HEIGHT: number;
            remotePlayers: { [sid: string]: RemotePlayer };
            mySid: string; playerX: number; playerY: number;
            playerColor: string; nickname: string; myStatus: string;
            keys: { [key: string]: boolean };
            speechBubbles: Map<string, SpeechBubble>;
            pokeAnimations: Map<string, { startTime: number; fromX: number; fromY: number }>;
            typingPlayers: { [sid: string]: string };
            POKE_DURATION: number; BUBBLE_DURATION: number;
            isJumping: boolean; jumpStartTime: number;
            JUMP_DURATION: number; JUMP_HEIGHT: number;
            myShape?: string;
            currentRoom?: string;
            GRID_SIZE?: number;
            hoveringPortal?: boolean;
            hoveringCalendar?: boolean;
        }
    ) {
        this._frameTime = Date.now();

        ctx.fillStyle = '#303032';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.save();
        ctx.translate(-state.cameraX, -state.cameraY);

        if (state.mapLoaded && state.mapImage) {
            // 오프스크린 캔버스 캐시 사용 (없으면 생성)
            if (!this._mapCanvas) this.cacheMapImage(state.mapImage);
            const src = this._mapCanvas || state.mapImage;
            if (src.width > 0 && src.height > 0) {
                const sx = Math.max(0, Math.floor(state.cameraX));
                const sy = Math.max(0, Math.floor(state.cameraY));
                const sw = Math.min(canvas.width, state.MAP_WIDTH - sx);
                const sh = Math.min(canvas.height, state.MAP_HEIGHT - sy);
                if (sw > 0 && sh > 0) {
                    ctx.drawImage(src, sx, sy, sw, sh, sx, sy, sw, sh);
                }
            } else {
                ctx.fillStyle = '#E2E8F0';
                ctx.fillRect(0, 0, state.MAP_WIDTH, state.MAP_HEIGHT);
            }
        } else {
            ctx.fillStyle = '#E2E8F0';
            ctx.fillRect(0, 0, state.MAP_WIDTH, state.MAP_HEIGHT);
        }

        // 포탈 이펙트 렌더링
        this.renderPortalEffect(ctx, state);

        // 캘린더 타일 힌트 렌더링 (솔루션 센터만)
        this.renderCalendarHint(ctx, state);

        // 원격 플레이어 + 말풍선 (뷰포트 컬링)
        const cullPad = 80;
        const vpLeft = state.cameraX - cullPad;
        const vpRight = state.cameraX + canvas.width + cullPad;
        const vpTop = state.cameraY - cullPad;
        const vpBottom = state.cameraY + canvas.height + cullPad;
        for (const sid in state.remotePlayers) {
            const p = state.remotePlayers[sid];
            if (p.x < vpLeft || p.x > vpRight || p.y < vpTop || p.y > vpBottom) continue;
            this.drawRemotePlayer(ctx, p, state.pokeAnimations, state.POKE_DURATION);
            const rJump = (p.isJumping && p.jumpStartTime) ? this.getJumpOffset(true, p.jumpStartTime, 400, 24) : 0;
            this.drawBubbleForPlayer(ctx, sid, p.x, p.y - rJump, state.speechBubbles);
        }

        // 내 플레이어 + 말풍선
        this.drawMyPlayer(ctx, state);
        const myJump = this.getJumpOffset(state.isJumping, state.jumpStartTime, state.JUMP_DURATION, state.JUMP_HEIGHT);
        this.drawBubbleForPlayer(ctx, state.mySid, state.playerX, state.playerY - myJump, state.speechBubbles);

        // 입력 중 표시
        for (const sid in state.typingPlayers) {
            if (state.speechBubbles.has(sid)) continue;
            let px: number, py: number;
            if (sid === state.mySid) { px = state.playerX; py = state.playerY - myJump; }
            else if (state.remotePlayers[sid]) {
                const tp = state.remotePlayers[sid];
                const tJump = (tp.isJumping && tp.jumpStartTime) ? this.getJumpOffset(true, tp.jumpStartTime, 400, 24) : 0;
                px = tp.x; py = tp.y - tJump;
            }
            else continue;
            this.drawTypingBubble(ctx, px, py);
        }

        // 찌르기 이모지
        this.renderPokeEmojis(ctx, state);

        ctx.restore();
    }

    // ===== 캐릭터 =====

    public drawCharacter(ctx: CanvasRenderingContext2D, x: number, y: number, color: string, name: string, isMe: boolean, status: string = 'in', isMoving: boolean = false, keys: { [key: string]: boolean } = {}, jumpOffset: number = 0, shape: string = 'circle') {
        const size = this.playerSize;
        const statusColor = getStatusColor(status);

        // 점프 오프셋 적용 (y를 위로 올림)
        const drawY = y - jumpOffset;

        // 그림자 (점프 시 그림자 작아짐)
        const shadowScale = jumpOffset > 0 ? Math.max(0.4, 1 - jumpOffset / 40) : 1;
        ctx.beginPath();
        ctx.ellipse(x, y + size / 2 + 6, (size / 2 + 4) * shadowScale, (size / 6) * shadowScale, 0, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,0,0,${0.1 * shadowScale})`;
        ctx.fill();

        // 발 (걷기/점프 애니메이션)
        const footY = drawY + size / 2 + 2;
        const footSize = 5;
        let footSpread = 6;
        let leftFootOffset = 0;
        let rightFootOffset = 0;
        let footYOffset = 0;
        if (jumpOffset > 0) {
            const jumpRatio = Math.min(jumpOffset / 20, 1);
            footSpread = 6 + 5 * jumpRatio;
            footYOffset = -3 * jumpRatio;
        } else if (isMoving) {
            const walkCycle = this._frameTime * 0.008;
            leftFootOffset = Math.sin(walkCycle) * 4;
            rightFootOffset = Math.sin(walkCycle + Math.PI) * 4;
        }

        ctx.beginPath();
        ctx.ellipse(x - footSpread + leftFootOffset, footY + 2 + footYOffset, footSize, footSize * 0.65, 0, 0, Math.PI * 2);
        ctx.fillStyle = this.darkenColorCached(color, 50);
        ctx.fill();

        ctx.beginPath();
        ctx.ellipse(x + footSpread + rightFootOffset, footY + 2 + footYOffset, footSize, footSize * 0.65, 0, 0, Math.PI * 2);
        ctx.fillStyle = this.darkenColorCached(color, 50);
        ctx.fill();

        // 몸통 (shape별 분기) — 단색 + 하이라이트
        ctx.beginPath();
        const r = size / 2;
        switch (shape) {
            case 'square':
                this.roundedRect(ctx, x - r, drawY - r, size, size, 8);
                break;
            case 'triangle':
                this.roundedTriangle(ctx, x, drawY, r);
                break;
            case 'heart':
                this.heartPath(ctx, x, drawY, r);
                break;
            case 'star':
                this.starPath(ctx, x, drawY, r);
                break;
            default: // circle
                ctx.arc(x, drawY, r, 0, Math.PI * 2);
                break;
        }
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = this.darkenColorCached(color, 30);
        ctx.lineWidth = 2;
        ctx.stroke();

        // 하이라이트 (gradient 대신 작은 원)
        ctx.beginPath();
        ctx.arc(x - 4, drawY - 4, r * 0.35, 0, Math.PI * 2);
        ctx.fillStyle = this.lightenColorCached(color, 60);
        ctx.globalAlpha = 0.35;
        ctx.fill();
        ctx.globalAlpha = 1.0;

        // 기본 눈
        this.drawDefaultEyes(ctx, x, drawY, isMe, keys);

        // 닉네임
        ctx.font = 'bold 14px SUIT, sans-serif';
        const textWidth = this.measureTextCached(ctx, name, 'bold 14px SUIT, sans-serif');
        const nameY = drawY - size / 2 - 12;
        const textHeight = 10; // 대략적인 고정값 (폰트 14px 기준)
        const textCenterY = nameY - textHeight / 2;

        // 상태 인디케이터 (닉네임 왼쪽, 텍스트 중앙선 정렬)
        const baseDotRadius = 5.5;
        const pulse = Math.sin(this._frameTime * 0.004) * 0.15 + 1.0;
        const dotRadius = baseDotRadius * pulse;
        const dotGap = 6;
        const dotX = x - textWidth / 2 - dotGap - baseDotRadius;
        const dotY = textCenterY;

        ctx.beginPath();
        ctx.arc(dotX, dotY, dotRadius + 2, 0, Math.PI * 2);
        ctx.fillStyle = statusColor;
        ctx.globalAlpha = 0.2 * pulse;
        ctx.fill();
        ctx.globalAlpha = 1.0;

        ctx.beginPath();
        ctx.arc(dotX, dotY, dotRadius, 0, Math.PI * 2);
        ctx.fillStyle = statusColor;
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.9)';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        ctx.fillStyle = isMe ? '#1D4ED8' : '#334155';
        ctx.textAlign = 'center';
        ctx.shadowColor = 'rgba(255,255,255,0.9)';
        ctx.shadowBlur = 4;
        ctx.fillText(name, x, nameY);
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.textAlign = 'start';
    }

    private drawDefaultEyes(ctx: CanvasRenderingContext2D, x: number, y: number, isMe: boolean, keys: { [key: string]: boolean }) {
        let eyeOffsetX = 0;
        let eyeOffsetY = 0;
        if (isMe) {
            if (keys['up']) eyeOffsetY = -3;
            if (keys['down']) eyeOffsetY = 3;
            if (keys['left']) eyeOffsetX = -3;
            if (keys['right']) eyeOffsetX = 3;
        }

        // 왼쪽 눈
        ctx.beginPath();
        ctx.arc(x - 5 + eyeOffsetX, y - 3 + eyeOffsetY, 3, 0, Math.PI * 2);
        ctx.fillStyle = '#FFFFFF';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x - 5 + eyeOffsetX * 1.2, y - 3 + eyeOffsetY * 1.2, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = '#1E293B';
        ctx.fill();

        // 오른쪽 눈
        ctx.beginPath();
        ctx.arc(x + 5 + eyeOffsetX, y - 3 + eyeOffsetY, 3, 0, Math.PI * 2);
        ctx.fillStyle = '#FFFFFF';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x + 5 + eyeOffsetX * 1.2, y - 3 + eyeOffsetY * 1.2, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = '#1E293B';
        ctx.fill();
    }

    private getJumpOffset(isJumping: boolean, jumpStartTime: number, jumpDuration: number, jumpHeight: number): number {
        if (!isJumping) return 0;
        const elapsed = this._frameTime - jumpStartTime;
        if (elapsed >= jumpDuration) return 0;
        const t = elapsed / jumpDuration;
        // 단조 포물선: 부드러운 상승/하강, 착지 시 튕김 느낌 없음
        return 4 * t * (1 - t) * jumpHeight;
    }

    private drawMyPlayer(ctx: CanvasRenderingContext2D, state: any) {
        let drawX = state.playerX;
        let drawY = state.playerY;
        const pokeAnim = state.pokeAnimations.get(state.mySid);
        if (pokeAnim) {
            const elapsed = this._frameTime - pokeAnim.startTime;
            if (elapsed > state.POKE_DURATION) {
                state.pokeAnimations.delete(state.mySid);
            } else {
                const shake = Math.sin(elapsed * 0.05) * 4 * (1 - elapsed / state.POKE_DURATION);
                drawX += shake;
            }
        }
        // 점프 처리
        const jumpOffset = this.getJumpOffset(state.isJumping, state.jumpStartTime, state.JUMP_DURATION, state.JUMP_HEIGHT);
        if (state.isJumping && jumpOffset === 0) {
            state.isJumping = false;
        }
        const myMoving = state.keys['up'] || state.keys['down'] || state.keys['left'] || state.keys['right'];
        this.drawCharacter(ctx, drawX, drawY, state.playerColor, state.nickname, true, state.myStatus, myMoving, state.keys, jumpOffset, state.myShape || 'circle');
    }

    private drawRemotePlayer(ctx: CanvasRenderingContext2D, player: RemotePlayer, pokeAnimations: Map<string, any>, pokeDuration: number) {
        let drawX = player.x;
        let drawY = player.y;
        const pokeAnim = pokeAnimations.get(player.sid);
        if (pokeAnim) {
            const elapsed = this._frameTime - pokeAnim.startTime;
            if (elapsed > pokeDuration) {
                pokeAnimations.delete(player.sid);
            } else {
                const shake = Math.sin(elapsed * 0.05) * 4 * (1 - elapsed / pokeDuration);
                drawX += shake;
            }
        }
        // 리모트 점프 처리
        let jumpOffset = 0;
        if (player.isJumping && player.jumpStartTime) {
            const elapsed = this._frameTime - player.jumpStartTime;
            if (elapsed >= 400) {
                player.isJumping = false;
            } else {
                jumpOffset = this.getJumpOffset(true, player.jumpStartTime, 400, 24);
            }
        }
        const remoteMoving = Math.abs(player.targetX - player.x) > 1 || Math.abs(player.targetY - player.y) > 1;
        this.drawCharacter(ctx, drawX, drawY, player.color, player.nickname, false, player.status, remoteMoving, {}, jumpOffset, player.shape || 'circle');
    }

    // ===== 말풍선 =====

    private drawBubbleForPlayer(ctx: CanvasRenderingContext2D, sid: string, x: number, y: number, speechBubbles: Map<string, SpeechBubble>) {
        const bubble = speechBubbles.get(sid);
        if (!bubble) return;

        if (this._frameTime > bubble.expireAt) {
            speechBubbles.delete(sid);
            return;
        }

        this.drawSpeechBubble(ctx, x, y, bubble.message, bubble.scope);
    }

    private getBubbleColors(scope?: string): { bg: string, text: string, border: string, shadow: string } {
        if (scope === 'global') return { bg: 'rgba(236, 253, 245, 0.95)', text: '#065F46', border: 'rgba(16, 185, 129, 0.2)', shadow: 'rgba(16, 185, 129, 0.12)' };
        if (scope === 'whisper') return { bg: 'rgba(245, 243, 255, 0.95)', text: '#5B21B6', border: 'rgba(167, 139, 250, 0.2)', shadow: 'rgba(167, 139, 250, 0.12)' };
        return { bg: 'rgba(240, 249, 255, 0.95)', text: '#0C4A6E', border: 'rgba(56, 189, 248, 0.2)', shadow: 'rgba(56, 189, 248, 0.12)' };
    }

    private drawSpeechBubble(ctx: CanvasRenderingContext2D, x: number, y: number, message: string, scope?: string) {
        ctx.font = '12px SUIT, sans-serif';
        const maxLineWidth = 140;

        // 줄바꿈 캐싱
        let lines = this._bubbleLineCache.get(message);
        if (!lines) {
            lines = [];
            let current = '';
            for (let i = 0; i < message.length; i++) {
                const ch = message[i];
                const testLine = current + ch;
                if (ctx.measureText(testLine).width > maxLineWidth && current.length > 0) {
                    lines.push(current);
                    current = ch;
                } else {
                    current = testLine;
                }
            }
            if (current) lines.push(current);
            this._bubbleLineCache.set(message, lines);
            // 캐시 크기 제한
            if (this._bubbleLineCache.size > 100) {
                const first = this._bubbleLineCache.keys().next().value;
                if (first !== undefined) this._bubbleLineCache.delete(first);
            }
        }
        const lineHeight = 17;
        const padding = 10;
        const triangleSize = 6;

        let maxWidth = 0;
        for (const line of lines) {
            const w = ctx.measureText(line).width;
            if (w > maxWidth) maxWidth = w;
        }

        const bubbleWidth = maxWidth + padding * 2;
        const bubbleHeight = lines.length * lineHeight + padding * 2;

        const nameTop = y - this.playerSize / 2 - 20;
        const bubbleGap = 6;
        const bubbleBottom = nameTop - triangleSize - bubbleGap;
        const bubbleTop = bubbleBottom - bubbleHeight;
        const bubbleLeft = x - bubbleWidth / 2;

        const colors = this.getBubbleColors(scope);

        ctx.save();
        ctx.fillStyle = colors.bg;
        ctx.shadowColor = colors.shadow;
        ctx.shadowBlur = 8;
        ctx.shadowOffsetY = 2;
        ctx.beginPath();
        this.roundRect(ctx, bubbleLeft, bubbleTop, bubbleWidth, bubbleHeight, 10);
        ctx.fill();
        ctx.restore();

        ctx.strokeStyle = colors.border;
        ctx.lineWidth = 1;
        ctx.beginPath();
        this.roundRect(ctx, bubbleLeft, bubbleTop, bubbleWidth, bubbleHeight, 10);
        ctx.stroke();

        ctx.fillStyle = colors.bg;
        ctx.beginPath();
        ctx.moveTo(x - triangleSize, bubbleBottom);
        ctx.lineTo(x, bubbleBottom + triangleSize);
        ctx.lineTo(x + triangleSize, bubbleBottom);
        ctx.closePath();
        ctx.fill();

        ctx.strokeStyle = colors.border;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x - triangleSize, bubbleBottom);
        ctx.lineTo(x, bubbleBottom + triangleSize);
        ctx.lineTo(x + triangleSize, bubbleBottom);
        ctx.stroke();

        ctx.fillStyle = colors.bg;
        ctx.fillRect(x - triangleSize + 1, bubbleBottom - 1, (triangleSize - 1) * 2, 2);

        ctx.fillStyle = colors.text;
        ctx.font = '12px SUIT, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        for (let i = 0; i < lines.length; i++) {
            ctx.fillText(lines[i], x, bubbleTop + padding + i * lineHeight);
        }
        ctx.textAlign = 'start';
        ctx.textBaseline = 'alphabetic';
    }

    private drawTypingBubble(ctx: CanvasRenderingContext2D, x: number, y: number) {
        const bubbleWidth = 48;
        const bubbleHeight = 26;
        const nameTop = y - this.playerSize / 2 - 20;
        const triangleSize = 5;
        const bubbleGap = 6;
        const bubbleBottom = nameTop - triangleSize - bubbleGap;
        const bubbleTop = bubbleBottom - bubbleHeight;
        const bubbleLeft = x - bubbleWidth / 2;

        ctx.save();
        ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
        ctx.shadowBlur = 6;
        ctx.shadowOffsetY = 2;
        ctx.beginPath();
        this.roundRect(ctx, bubbleLeft, bubbleTop, bubbleWidth, bubbleHeight, 13);
        ctx.fill();
        ctx.restore();

        ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
        ctx.beginPath();
        ctx.moveTo(x - triangleSize, bubbleBottom);
        ctx.lineTo(x, bubbleBottom + triangleSize);
        ctx.lineTo(x + triangleSize, bubbleBottom);
        ctx.closePath();
        ctx.fill();

        const time = this._frameTime * 0.004;
        const dotY = bubbleTop + bubbleHeight / 2;
        for (let i = 0; i < 3; i++) {
            const bounce = Math.sin(time + i * 1.0) * 3;
            ctx.beginPath();
            ctx.arc(x - 10 + i * 10, dotY + bounce, 3.5, 0, Math.PI * 2);
            ctx.fillStyle = '#9CA3AF';
            ctx.fill();
        }
    }

    // ===== 찌르기 이모지 =====

    private renderPokeEmojis(ctx: CanvasRenderingContext2D, state: any) {
        for (const [targetSid, anim] of state.pokeAnimations.entries()) {
            const elapsed = this._frameTime - anim.startTime;
            if (elapsed > state.POKE_DURATION) {
                state.pokeAnimations.delete(targetSid);
                continue;
            }

            let targetX: number, targetY: number;
            if (targetSid === state.mySid) {
                targetX = state.playerX;
                targetY = state.playerY;
            } else if (state.remotePlayers[targetSid]) {
                targetX = state.remotePlayers[targetSid].x;
                targetY = state.remotePlayers[targetSid].y;
            } else {
                state.pokeAnimations.delete(targetSid);
                continue;
            }

            const progress = elapsed / state.POKE_DURATION;
            const dx = targetX - anim.fromX;
            const dy = targetY - anim.fromY;
            const angle = Math.atan2(dy, dx);

            const emojiProgress = Math.min(progress * 2, 1);
            const emojiX = anim.fromX + dx * emojiProgress;
            const emojiY = anim.fromY + dy * emojiProgress;
            const opacity = progress < 0.5 ? 1 : 1 - (progress - 0.5) * 2;

            ctx.save();
            ctx.globalAlpha = opacity;
            ctx.font = '24px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.translate(emojiX, emojiY - 20);
            ctx.rotate(angle);
            ctx.fillText('👉', 0, 0);
            ctx.restore();
        }
    }

    // ===== 유틸 =====

    private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + w - r, y);
        ctx.arcTo(x + w, y, x + w, y + r, r);
        ctx.lineTo(x + w, y + h - r);
        ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
        ctx.lineTo(x + r, y + h);
        ctx.arcTo(x, y + h, x, y + h - r, r);
        ctx.lineTo(x, y + r);
        ctx.arcTo(x, y, x + r, y, r);
    }

    // ===== 모양 헬퍼 =====

    private roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
    }

    private roundedTriangle(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number) {
        const s = r * 1.25;
        const pts = [
            { x: cx, y: cy - s },
            { x: cx + s * 0.87, y: cy + s * 0.5 },
            { x: cx - s * 0.87, y: cy + s * 0.5 },
        ];
        const br = 4;
        ctx.moveTo((pts[0].x + pts[2].x) / 2, (pts[0].y + pts[2].y) / 2);
        for (let i = 0; i < 3; i++) {
            const p1 = pts[i];
            const p2 = pts[(i + 1) % 3];
            ctx.arcTo(p1.x, p1.y, p2.x, p2.y, br);
        }
        ctx.closePath();
    }

    private heartPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number) {
        const s = r * 0.85;
        ctx.moveTo(cx, cy + s);
        ctx.bezierCurveTo(cx - s * 2, cy - s * 0.5, cx - s, cy - s * 1.8, cx, cy - s * 0.8);
        ctx.bezierCurveTo(cx + s, cy - s * 1.8, cx + s * 2, cy - s * 0.5, cx, cy + s);
        ctx.closePath();
    }

    private starPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number) {
        const spikes = 5;
        const outerR = r * 1.2;
        const innerR = r * 0.55;
        let rot = -Math.PI / 2;
        ctx.moveTo(cx + Math.cos(rot) * outerR, cy + Math.sin(rot) * outerR);
        for (let i = 0; i < spikes; i++) {
            ctx.lineTo(cx + Math.cos(rot) * outerR, cy + Math.sin(rot) * outerR);
            rot += Math.PI / spikes;
            ctx.lineTo(cx + Math.cos(rot) * innerR, cy + Math.sin(rot) * innerR);
            rot += Math.PI / spikes;
        }
        ctx.closePath();
    }

    // ===== 포탈 이펙트 =====

    private renderCalendarHint(ctx: CanvasRenderingContext2D, state: any) {
        if (state.currentRoom !== 'solution') return;
        const gs = state.GRID_SIZE || 40;
        const hovering = state.hoveringCalendar || false;
        const time = this._frameTime * 0.001;

        // 캘린더 타일: row 11~12, col 18~22
        const x = 18 * gs;
        const y = 11 * gs;
        const w = 5 * gs;
        const h = 2 * gs;

        // hover 시에만 글로우 + 라벨 표시
        if (hovering) {
            const pulse = Math.sin(time * 2.5) * 0.1 + 0.9;
            ctx.save();
            ctx.shadowColor = 'rgba(59, 130, 246, 0.5)';
            ctx.shadowBlur = 14;
            ctx.fillStyle = `rgba(59, 130, 246, ${0.12 * pulse})`;
            ctx.beginPath();
            this.roundRect(ctx, x - 2, y - 2, w + 4, h + 4, 6);
            ctx.fill();
            ctx.restore();

            ctx.strokeStyle = `rgba(59, 130, 246, ${0.5 * pulse})`;
            ctx.lineWidth = 2;
            ctx.beginPath();
            this.roundRect(ctx, x, y, w, h, 4);
            ctx.stroke();

            // 라벨
            const cx = x + w / 2;
            const cy = y - 14;
            ctx.font = 'bold 13px SUIT, sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const label = '\uD83D\uDCC5 Outlook Calendar';
            const tw = ctx.measureText(label).width + 16;
            const th = 22;
            ctx.fillStyle = 'rgba(30, 58, 138, 0.85)';
            ctx.beginPath();
            this.roundRect(ctx, cx - tw / 2, cy - th / 2, tw, th, 6);
            ctx.fill();
            ctx.fillStyle = '#BFDBFE';
            ctx.fillText(label, cx, cy);
            ctx.textAlign = 'start';
            ctx.textBaseline = 'alphabetic';
        }
    }

    private renderPortalEffect(ctx: CanvasRenderingContext2D, state: any) {
        const gs = state.GRID_SIZE || 40;
        const time = this._frameTime * 0.001;
        const hovering = state.hoveringPortal || false;

        if (state.currentRoom === 'solution') {
            // 솔루션→연구: row 13~16, col 54 (세로 4칸)
            const x = 54 * gs;
            const y = 13 * gs;
            const w = gs;
            const h = 4 * gs;
            this.drawPortalGlow(ctx, x, y, w, h, hovering, time);
            this.drawPortalText(ctx, x + w / 2, y + h / 2, 'AI 연구센터', 'horizontal', time, hovering);
        } else if (state.currentRoom === 'research') {
            // 연구→솔루션: row 28, col 39~42 (가로 4칸)
            const x = 39 * gs;
            const y = 28 * gs;
            const w = 4 * gs;
            const h = gs;
            this.drawPortalGlow(ctx, x, y, w, h, hovering, time);
            this.drawPortalText(ctx, x + w / 2, y + h / 2, 'AI 솔루션센터', 'vertical', time, hovering);
        }
    }

    private drawPortalGlow(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, hovering: boolean, time: number) {
        const pulse = Math.sin(time * 2) * 0.15 + 0.85;
        const baseAlpha = hovering ? 0.35 : 0.18;
        const alpha = baseAlpha * pulse;

        // 외부 글로우
        ctx.save();
        ctx.shadowColor = 'rgba(255, 140, 0, 0.6)';
        ctx.shadowBlur = hovering ? 20 : 12;
        ctx.fillStyle = `rgba(255, 140, 0, ${alpha})`;
        ctx.beginPath();
        this.roundRect(ctx, x - 2, y - 2, w + 4, h + 4, 6);
        ctx.fill();
        ctx.restore();

        // 내부 하이라이트
        const innerAlpha = hovering ? 0.25 : 0.1;
        ctx.fillStyle = `rgba(255, 179, 71, ${innerAlpha * pulse})`;
        ctx.beginPath();
        this.roundRect(ctx, x, y, w, h, 4);
        ctx.fill();

        // 보더
        ctx.strokeStyle = `rgba(255, 165, 0, ${(hovering ? 0.7 : 0.4) * pulse})`;
        ctx.lineWidth = hovering ? 2.5 : 1.5;
        ctx.beginPath();
        this.roundRect(ctx, x, y, w, h, 4);
        ctx.stroke();
    }

    private drawPortalText(ctx: CanvasRenderingContext2D, cx: number, cy: number, text: string, direction: 'horizontal' | 'vertical', time: number, hovering: boolean) {
        ctx.save();
        // 텍스트는 깜빡이지 않음 (호버 시 밝아지기만)
        ctx.globalAlpha = hovering ? 1 : 0.95;
        ctx.font = `bold ${hovering ? 16 : 15}px SUIT, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // 텍스트 배경
        const metrics = ctx.measureText(text);
        const tw = metrics.width + 20;
        const th = 26;
        ctx.fillStyle = 'rgba(40, 15, 0, 0.82)';
        ctx.beginPath();
        this.roundRect(ctx, cx - tw / 2, cy - th / 2, tw, th, 8);
        ctx.fill();

        // 텍스트 (주황 계열, 밝게)
        ctx.fillStyle = '#FFCC66';
        ctx.shadowColor = 'rgba(255, 160, 0, 0.9)';
        ctx.shadowBlur = 8;
        ctx.fillText(text, cx, cy);
        ctx.shadowBlur = 0;

        // 화살표 애니메이션 (화살표만 움직임)
        const arrowAlpha = Math.sin(time * 3) * 0.2 + 0.8;
        const arrowOffset = Math.sin(time * 3) * 6;
        ctx.fillStyle = `rgba(255, 200, 80, ${arrowAlpha})`;
        if (direction === 'horizontal') {
            ctx.font = 'bold 22px SUIT, sans-serif';
            ctx.shadowColor = 'rgba(255, 140, 0, 0.8)';
            ctx.shadowBlur = 6;
            ctx.fillText('→', cx + tw / 2 + 12 + arrowOffset, cy);
        } else {
            // 연구센터 아래 화살표: 맵 아래쪽으로 크게 렌더링
            ctx.font = 'bold 34px SUIT, sans-serif';
            ctx.shadowColor = 'rgba(255, 140, 0, 0.9)';
            ctx.shadowBlur = 10;
            ctx.fillText('↓', cx, cy + th / 2 + 24 + arrowOffset);
        }

        ctx.restore();
    }

    public lightenColor(hex: string, amount: number): string {
        const num = parseInt(hex.replace('#', ''), 16);
        const r = Math.min(255, (num >> 16) + amount);
        const g = Math.min(255, ((num >> 8) & 0x00FF) + amount);
        const b = Math.min(255, (num & 0x0000FF) + amount);
        return `rgb(${r},${g},${b})`;
    }

    public darkenColor(hex: string, amount: number): string {
        const num = parseInt(hex.replace('#', ''), 16);
        const r = Math.max(0, (num >> 16) - amount);
        const g = Math.max(0, ((num >> 8) & 0x00FF) - amount);
        const b = Math.max(0, (num & 0x0000FF) - amount);
        return `rgb(${r},${g},${b})`;
    }

    private lightenColorCached(hex: string, amount: number): string {
        const key = `l:${hex}:${amount}`;
        let c = this._colorCache.get(key);
        if (!c) { c = this.lightenColor(hex, amount); this._colorCache.set(key, c); }
        return c;
    }

    private darkenColorCached(hex: string, amount: number): string {
        const key = `d:${hex}:${amount}`;
        let c = this._colorCache.get(key);
        if (!c) { c = this.darkenColor(hex, amount); this._colorCache.set(key, c); }
        return c;
    }

    private measureTextCached(ctx: CanvasRenderingContext2D, text: string, font: string): number {
        const key = `${font}:${text}`;
        let w = this._textWidthCache.get(key);
        if (w === undefined) { ctx.font = font; w = ctx.measureText(text).width; this._textWidthCache.set(key, w); }
        return w;
    }
}
